import { ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import { useLazyQuery } from '@vue/apollo-composable';

import ListUserProjects from '@/gql/projects/ListUserProjects.gql';

export const useProjectStore = defineStore(
  'projects',
  () => {
    const route = useRoute();

    const savedProjectId = ref(null);

    const paramProjectId = computed(() => route.params.projectId);

    watch(paramProjectId, (value) => {
      if (value) {
        savedProjectId.value = value;
      }
    });

    const currentProjectId = computed(() => {
      if (paramProjectId.value) return paramProjectId.value;
      if (savedProjectId.value) return savedProjectId.value;
      if (projects.value.length > 0) return projects.value[0].project_id;
      return null;
    });

    const currentProject = computed(() => {
      if (!projects.value.length) return null;
      return projects.value.find(
        (v) => v.project_id === currentProjectId.value
      );
    });

    const projects = ref([]);

    const currentProjectRole = computed(() => {
      return currentProject.value ? currentProject.value.role : null;
    });

    function updateProjects(data) {
      projects.value = [...data];
      if (!savedProjectId.value) {
        savedProjectId.value = projects.value[0].project_id;
      }
    }

    const { load, refetch, onError, loading, onResult } =
      useLazyQuery(ListUserProjects);

    function fetchProjects(forceRefetch) {
      return new Promise((resolve, reject) => {
        onResult(({ data }) => {
          if (data) {
            updateProjects(data.projects);
            resolve(data.projects);
          }
        });
        onError((error) => {
          reject(error);
        });
        if (forceRefetch) {
          load() || refetch();
        } else {
          load();
        }
      });
    }

    function setCurrentProjectById(projectId) {
      if (projectId) savedProjectId.value = projectId;
    }

    function reset() {
      projects.value = [];
      savedProjectId.value = null;
    }

    return {
      projects,
      projectsLoading: loading,
      savedProjectId,
      currentProject,
      currentProjectId,
      currentProjectRole,
      fetchProjects,
      updateProjects,
      setCurrentProjectById,
      reset,
    };
  },
  {
    persist: [
      {
        paths: ['projects', 'savedProjectId'],
        storage: localStorage,
      },
      {
        paths: ['projects', 'savedProjectId'],
        storage: sessionStorage,
      },
    ],
  }
);
